export interface MultipartPart { type: string; data: Uint8Array; }

export function headerParameter(header: string, name: string): string | undefined {
  const escapedName = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const match = new RegExp(`(?:^|;)\\s*${escapedName}\\s*=\\s*(?:"([^"]*)"|([^;\\s]+))`, 'i').exec(header);
  return match?.[1] ?? match?.[2];
}

/** Scan delimiters as bytes; body views share the response buffer without copying it. */
export function parseMultipartBody(bytes: Uint8Array, boundary: string): MultipartPart[] {
  if (!boundary || /[\r\n]/.test(boundary)) throw new Error('Invalid multipart boundary');
  const marker = new TextEncoder().encode(`--${boundary}`);
  const decoder = new TextDecoder('utf-8');
  const findBoundary = (from: number): number => {
    for (let i = bytes.indexOf(45, from); i >= 0; i = bytes.indexOf(45, i + 1)) {
      if (i !== 0 && (bytes[i - 2] !== 13 || bytes[i - 1] !== 10)) continue;
      let j = 0;
      while (j < marker.length && bytes[i + j] === marker[j]) j++;
      if (j !== marker.length) continue;
      const end = i + j;
      if (bytes[end] === 13 && bytes[end + 1] === 10) return i;
      if (bytes[end] === 45 && bytes[end + 1] === 45 &&
        (end + 2 === bytes.length || (bytes[end + 2] === 13 && bytes[end + 3] === 10))) return i;
    }
    return -1;
  };
  const parts: MultipartPart[] = [];
  let position = findBoundary(0);
  if (position < 0) throw new Error('Multipart boundary not found');
  while (position >= 0) {
    const start = position + marker.length;
    if (bytes[start] === 45 && bytes[start + 1] === 45) return parts;
    const headerStart = start + 2;
    let headerEnd = headerStart;
    while (headerEnd + 3 < bytes.length && !(bytes[headerEnd] === 13 && bytes[headerEnd + 1] === 10 &&
      bytes[headerEnd + 2] === 13 && bytes[headerEnd + 3] === 10)) headerEnd++;
    if (headerEnd + 3 >= bytes.length) throw new Error('Incomplete multipart headers');
    const headers = decoder.decode(bytes.subarray(headerStart, headerEnd));
    // This API labels JSON fields and ZIP attachments in Content-Disposition.
    // Retain support for responses carrying the same metadata in Content-Type.
    const disposition = /^content-disposition:\s*(.+)$/im.exec(headers)?.[1]?.trim();
    const type = /^(?:attachment|application\/json)\s*(?:;|$)/i.test(disposition ?? '')
      ? disposition : /^content-type:\s*(.+)$/im.exec(headers)?.[1]?.trim();
    if (!type) throw new Error('Missing multipart content type');
    const bodyStart = headerEnd + 4;
    position = findBoundary(bodyStart);
    if (position < 0 || position < bodyStart + 2) throw new Error('Incomplete multipart body');
    parts.push({ type, data: bytes.subarray(bodyStart, position - 2) });
  }
  throw new Error('Missing multipart closing boundary');
}
